import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, signal, untracked } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { MsfInput } from '@components/ui/forms/input/input';
import { jsonTreeAnnotationKey } from './json-tree-annotations';
import { attachJsonTreeNotes, expandableIds, parseJsonTree, projectJsonTree } from './json-tree.util';

@Component({
  selector: 'app-json-tree',
  standalone: true,
  imports: [TranslateModule, SecondaryButton, MsfInput, NgTemplateOutlet],
  templateUrl: './json-tree.html',
  styleUrl: './json-tree.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JsonTreeComponent implements OnDestroy {
  private readonly translate = inject(TranslateService);
  readonly label = input('');
  readonly text = input('');
  readonly heading = input<'h3' | 'h4'>('h3');

  readonly search = signal('');
  readonly expanded = signal<ReadonlySet<string>>(new Set());
  readonly copied = signal(false);
  readonly language = signal(this.translate.currentLang || this.translate.defaultLang || 'en-US');

  readonly tree = computed(() => {
    this.language();
    const parsed = parseJsonTree(this.text());
    if (!parsed) {
      return null;
    }
    return attachJsonTreeNotes(parsed, key => {
      const annotationKey = jsonTreeAnnotationKey(key);
      return annotationKey ? this.translate.instant(annotationKey) : null;
    });
  });
  readonly isHeading3 = computed(() => this.heading() === 'h3');
  readonly view = computed(() => {
    const root = this.tree();
    return root ? projectJsonTree(root, this.expanded(), this.search()) : null;
  });
  readonly canCopy = computed(() => !!this.text().trim());
  readonly copyTitle = computed(() => this.copied() ? 'Copied' : 'Copy JSON');
  readonly copyIcon = computed(() => this.copied() ? 'pi pi-check' : 'pi pi-copy');

  private resetTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(event => {
      this.language.set(event.lang);
    });
    effect(() => {
      const root = this.tree();
      this.text();
      untracked(() => {
        this.expanded.set(new Set(root?.expandable ? [root.id] : []));
        this.search.set('');
      });
    });
  }

  expandAll(): void {
    const root = this.tree();
    this.expanded.set(new Set(root ? expandableIds(root) : []));
  }

  collapseAll(): void {
    this.expanded.set(new Set());
  }

  toggle(id: string): void {
    const next = new Set(this.expanded());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.expanded.set(next);
  }

  setSearch(value: string): void {
    this.search.set(value ?? '');
  }

  copy(): void {
    const value = this.text();
    if (!value.trim() || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }
    void navigator.clipboard.writeText(value).then(() => {
      this.copied.set(true);
      if (this.resetTimer) {
        clearTimeout(this.resetTimer);
      }
      this.resetTimer = setTimeout(() => {
        this.copied.set(false);
        this.resetTimer = null;
      }, 1500);
    });
  }

  ngOnDestroy(): void {
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
    }
  }
}
